import OnboardingHeader from '@jonni/components/Steps/OnboardingHeader';
import SideSteps from '@jonni/components/Steps/SideSteps';
import StepsMobile from '@jonni/components/Steps/StepsMobile';
import { useLogScreenView } from '@jonni/helpers/analytics';
import useMaintenance from '@jonni/hooks/useMaintenance';
import { OnboardingStepScreen, useOnboarding } from '@jonni/hooks/useOnboarding';
import useStepFromUrl from '@jonni/hooks/useStepFromUrl';
import {
  LoaderWrapper,
  OnboardingLayoutBackgroundWrapper,
  OnboardingLayoutBlueBackground,
  OnboardingLayoutBodyWrapper,
  OnboardingLayoutStepContainer,
  StepWrapper,
  StyledOverlay,
} from '@jonni/styles/signup/onboardingSteps';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useEffect, useMemo } from 'react';

export interface OnboardingLayoutProps {
  path: string;
  children: React.ReactNode;
}

const OnboardingLayout: React.FC<OnboardingLayoutProps> = ({ children, path }) => {
  useLogScreenView(path);
  useMaintenance();
  const stepFromUrl = useStepFromUrl(path);

  const {
    state: { currentUser, loading, currentScreen },
    isLoading,
    fetchCurrentUser,
  } = useOnboarding();

  useEffect(() => {
    void fetchCurrentUser();
  }, [fetchCurrentUser]);

  const shouldRenderStep = useMemo(() => {
    return (
      (currentUser?.id !== '' ||
        (stepFromUrl && stepFromUrl === OnboardingStepScreen.WELCOME) ||
        (stepFromUrl && stepFromUrl === OnboardingStepScreen.AUTHENTICATION) ||
        (stepFromUrl && stepFromUrl === OnboardingStepScreen.VERIFICATION)) &&
      currentScreen &&
      stepFromUrl &&
      currentScreen === stepFromUrl
    );
  }, [currentUser, currentScreen, stepFromUrl]);

  return (
    <>
      <OnboardingHeader />

      <OnboardingLayoutBackgroundWrapper>
        <OnboardingLayoutBlueBackground />
      </OnboardingLayoutBackgroundWrapper>

      <OnboardingLayoutBodyWrapper>
        <SideSteps activeStep={stepFromUrl || OnboardingStepScreen.WELCOME} />
        <StepsMobile activeStep={stepFromUrl || OnboardingStepScreen.WELCOME} />

        <OnboardingLayoutStepContainer>
          {isLoading || loading ? (
            <StyledOverlay>
              <LoaderWrapper>
                <CircularProgress size={60} />
              </LoaderWrapper>
            </StyledOverlay>
          ) : null}

          <OnboardingHeader isContentHeader />

          {shouldRenderStep ? <StepWrapper>{children}</StepWrapper> : null}
        </OnboardingLayoutStepContainer>
      </OnboardingLayoutBodyWrapper>
    </>
  );
};

OnboardingHeader.displayName = 'OnboardingHeader';

export default React.memo(
  OnboardingLayout,
  (prevProps: OnboardingLayoutProps, nextProps: OnboardingLayoutProps): boolean => {
    return prevProps.path === nextProps.path && prevProps.children === nextProps.children;
  },
);
